import React, { PropsWithChildren, useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { AmlValueDetailedDto } from 'api/Clients/Types/AmlValueDetailedDto'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { FlexBox } from 'components/Box'
import { Loading } from 'components/Loading'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { dateComparator, formatLocalDateUTC } from 'utils/date'
import { filterBySearchText } from 'utils/search'
import { formatBooleanValue } from 'utils/string.utils'

import TitleSearch from '../../../Administration/Users/TitleSearch'

interface ClientDetailsAMLScoreProps {
  data: ClientDetailedDto
}

const useClientDetailsAMLDetailedHistoryFetch = (props: ClientDetailsAMLScoreProps) => {
  const { data } = props

  const clientsApiClient = useApiClient(ClientsApiClient)

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(async () => {
    return clientsApiClient.getClientAMLDetailed(data.id)
  }, [data.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const { data: amlData, isLoading } = useFetchOne(fetchData)

  return { amlData, isLoading, forceRefresh }
}

interface AmlValueDetailed {
  valid: string
  customNote: string
  reasonName: string
  score: string
  userName: string
  createdDate: string
}

export const ClientDetailsAMLDetailedHistory: React.FC<ClientDetailsAMLScoreProps> = (props) => {
  const { amlData: data = [], isLoading } = useClientDetailsAMLDetailedHistoryFetch(props)

  const [amlData, setAmlData] = useState<AmlValueDetailed[]>([])

  useEffect(() => {
    setAmlData(createAmlData(data))
  }, [data, setAmlData])

  const handleSearch = (searchText: string) => {
    if (data) {
      const searchData = createAmlData(data)
      const searchResult = filterBySearchText(searchData)(searchText)
      setAmlData(searchResult)
    }
  }

  const gridState = amlData.sort((a, b) => dateComparator(a.createdDate, b.createdDate, true))

  return (
    <div>
      <Loading isLoading={isLoading}>
        <header>
          <label>Search</label>
          <TitleSearch placeholder='Search results' onSearch={handleSearch} />
        </header>
        <Grid resizable data={gridState} style={{ zIndex: 0 }}>
          <Column
            title='Status'
            field='valid'
            cell={(gridCellProps) => <StatusColumn {...gridCellProps} />}
            className='table-columns__content'
          />
          <Column title='Custom note' field='customNote' className='table-columns__content' />
          <Column title='Reason' field='reasonName' className='table-columns__content' />
          <Column title='Value' field='score' className='table-columns__number' />
          <Column title='User' field='userName' className='table-columns__content' />
          <Column
            title='Date (and time)'
            field='createdDate'
            cell={(gridCellProps) => (
              <td>{formatLocalDateUTC(gridCellProps.dataItem.createdDate, true)}</td>
            )}
          />
        </Grid>
      </Loading>
    </div>
  )
}

const StatusColumn: React.FC<PropsWithChildren<GridCellProps>> = (gridCellProps) => {
  if (formatBooleanValue(gridCellProps.dataItem.valid)) {
    return (
      <td align='center' className='table-columns__content'>
        <FlexBox justifyContent='center'>
          <Badge variant='green'>Valid</Badge>
        </FlexBox>
      </td>
    )
  }

  return (
    <td className='table-columns__content'>
      <FlexBox justifyContent='center'>
        <Badge variant='red'>Not Valid</Badge>
      </FlexBox>
    </td>
  )
}

const createAmlData = (data: AmlValueDetailedDto[] = []): AmlValueDetailed[] => {
  return data.map((x) => ({
    valid: x.valid?.toString(),
    customNote: x.customNote,
    reasonName: x?.reason?.name,
    score: x.score?.toString(),
    userName: x?.user?.name,
    createdDate: x?.createdDate,
  }))
}
